import { CrmIntegrationType } from '@alucio/aws-beacon-amplify/src/models';
import type { CustomFieldDefinition, CRMIntegration } from '@alucio/aws-beacon-amplify/src/models';
import type {
  CRMSubmitMeetingPayload,
  CRMSubmitResponseReferences,
  CRMSubmitStandaloneFormPayload,
  FormSettings,
} from './CRMIndexedDBTypes';
import type { SyncEntry } from './ISyncManifest';
import type { CustomFieldValuesMap, MeetingORM } from 'src/types/orms';
import type { CRMIntegrationSession } from '../../state/machines/CRM/util';
import type { VeevaVaultFormSetting } from './CRMVeevaVaultTypes';

export interface FormTranslatorResponse {
  isLoading?: boolean;
  crmCustomFields: CustomFieldDefinition[];
  crmDefaultValues: CustomFieldValuesMap;
}

export interface ICRMFormTranslator {
  getFormCustomFieldConfig: () => FormTranslatorResponse;
}

export interface ICRMSyncer {
  initialize(): SyncEntry[];
  processManifestEntry(entry: SyncEntry): Promise<SyncEntry[]>;
  configSyncComplete(entries: SyncEntry[]): Promise<(FormSettings | VeevaVaultFormSetting)[]>;
  submitToCRM(crmSubmitPayload: CRMSubmitMeetingPayload): Promise<CRMSubmitResponseReferences>;
  submitStandaloneFormToCRM(crmSubmitPayload: CRMSubmitStandaloneFormPayload): Promise<CRMSubmitResponseReferences>;
  handleDeleteMeeting(meeting: MeetingORM): Promise<CRMSubmitResponseReferences>;
}

export interface ICRMSessionHandler {
  logOut(crmSession?: CRMIntegrationSession): Promise<void>;
  keepSessionAlive?(): Promise<void>;
}

export type FormTranslatorPayload = {
  salesforceFormSettings: FormSettings[];
  veevaVaultFormSettings: VeevaVaultFormSetting[];
  primaryTable?: string;
}

class CRMHandlerClass {
  private crmIntegrationConfig: CRMIntegration;

  constructor(crmIntegrationConfig: CRMIntegration) {
    if (!crmIntegrationConfig.meetingSetting) {
      throw Error('No meetingSetting is defined.');
    }
    this.crmIntegrationConfig = crmIntegrationConfig;
  }

  Translator(formTranslatorPayload: FormTranslatorPayload): ICRMFormTranslator {
    const mainTable = formTranslatorPayload.primaryTable || this.crmIntegrationConfig.meetingSetting?.apiName || '';
    switch (this.crmIntegrationConfig.crmIntegrationType) {
      case CrmIntegrationType.SALESFORCE:
        return new (
          require('./Translators/SalesforceFormTranslator').SalesforceFormTranslator
        )(formTranslatorPayload, mainTable);
      case CrmIntegrationType.VEEVA:
        return new (
          require('./Translators/VeevaFormTranslator').VeevaFormTranslator
        )(formTranslatorPayload, mainTable);
      case CrmIntegrationType.VEEVA_VAULT:
        return new (
          require('./Translators/VeevaVault/VeevaVaultFormTranslator').VeevaVaultFormTranslator
        )(formTranslatorPayload, mainTable);
      default:
        throw new Error(`Unsupported CRM integration: ${this.crmIntegrationConfig.crmIntegrationType}`);
    }
  }

  Syncer(): ICRMSyncer {
    switch (this.crmIntegrationConfig.crmIntegrationType) {
      case CrmIntegrationType.SALESFORCE:
        return new (require('./Syncers/SalesforceSyncer').SalesforceSyncer)(this.crmIntegrationConfig);
      case CrmIntegrationType.VEEVA:
        return new (require('./Syncers/VeevaSyncer').VeevaSyncer)(this.crmIntegrationConfig);
      case CrmIntegrationType.VEEVA_VAULT:
        return new (require('./Syncers/VeevaVault/VeevaVaultSyncer').VeevaVaultSyncer)(this.crmIntegrationConfig);
      default:
        throw new Error(`Unsupported CRM integration: ${this.crmIntegrationConfig.crmIntegrationType}`);
    }
  }

  SessionHandler(): ICRMSessionHandler {
    switch (this.crmIntegrationConfig.crmIntegrationType) {
      case CrmIntegrationType.SALESFORCE:
      case CrmIntegrationType.VEEVA:
        return new (require('./SessionHandler/SalesforceSessionHandler').SalesforceSessionHandler)();
      case CrmIntegrationType.VEEVA_VAULT:
        return new (require('./SessionHandler/VeevaVaultSessionHandler').VeevaVaultSessionHandler)();
      default:
        throw new Error(`Unsupported CRM integration: ${this.crmIntegrationConfig.crmIntegrationType}`);
    }
  }
}

export const CRMHandler = (crmIntegrationConfig: CRMIntegration) =>
  new CRMHandlerClass(crmIntegrationConfig);
